import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, ToggleLeftIcon, ToggleRightIcon, InfoIcon, ClockIcon, UploadCloudIcon } from 'lucide-react';
import { CountrySelector } from '../../settings/CountrySelector';
import { CurrencySelector } from '../../settings/CurrencySelector';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import { TaxTypeSelector } from './types/TaxTypeSelector';
import type { OrgUnit } from '../sharedFinancialData';
import type { TaxType } from './types/TaxTypeSelector';
export function TaxCodeForm({
  taxCode = null,
  onSave,
  onCancel,
  onToggleStatus,
  locale = 'en-US',
  auditHistory = [],
  lifecycleHistory = [],
  taxTypes = []
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    rate: 0.0,
    taxTypeId: '',
    taxTypeName: '',
    country: '',
    jurisdiction: '',
    currencyCode: '',
    isExempt: false,
    isInclusive: false,
    scope: 'Global',
    orgUnitId: '',
    orgUnitName: '',
    orgUnitPath: '',
    globalTaxDefinitionId: ''
  });
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Initialize form with existing tax code data if editing
  useEffect(() => {
    if (taxCode) {
      setFormData({
        code: taxCode.code || '',
        description: taxCode.description || '',
        rate: taxCode.rate || 0.0,
        taxTypeId: taxCode.taxTypeId || '',
        taxTypeName: taxCode.taxTypeName || '',
        country: taxCode.country || '',
        jurisdiction: taxCode.jurisdiction || '',
        currencyCode: taxCode.currencyCode || '',
        isExempt: taxCode.isExempt || false,
        isInclusive: taxCode.isInclusive || false,
        scope: taxCode.scope || 'Global',
        orgUnitId: taxCode.orgUnitId || '',
        orgUnitName: taxCode.orgUnitName || '',
        orgUnitPath: taxCode.orgUnitPath || '',
        globalTaxDefinitionId: taxCode.globalTaxDefinitionId || ''
      });
    }
  }, [taxCode]);
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name === 'scope' && value === 'Global') {
      // Clear OrgUnit data when switching to Global
      setFormData({
        ...formData,
        scope: value,
        orgUnitId: '',
        orgUnitName: '',
        orgUnitPath: ''
      });
      setSelectedOrgUnit(null);
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
      // If isExempt is checked, set rate to 0
      if (name === 'isExempt' && checked) {
        setFormData({
          ...formData,
          [name]: checked,
          rate: 0
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    // Clear any error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleCountryChange = country => {
    setFormData({
      ...formData,
      country
    });
    if (errors.country) {
      setErrors({
        ...errors,
        country: null
      });
    }
  };
  const handleCurrencyChange = currencyCode => {
    setFormData({
      ...formData,
      currencyCode
    });
    if (errors.currencyCode) {
      setErrors({
        ...errors,
        currencyCode: null
      });
    }
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
    setFormData({
      ...formData,
      orgUnitId: orgUnitId,
      orgUnitName: orgUnit?.name || '',
      orgUnitPath: orgUnit?.path || ''
    });
    // Clear error
    if (errors.orgUnitId) {
      setErrors({
        ...errors,
        orgUnitId: null
      });
    }
  };
  const handleTaxTypeChange = (taxTypeId: string, taxType: TaxType | null) => {
    setFormData({
      ...formData,
      taxTypeId: taxTypeId,
      taxTypeName: taxType?.name || ''
    });
    // Clear error
    if (errors.taxTypeId) {
      setErrors({
        ...errors,
        taxTypeId: null
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};
    // Required fields
    if (!formData.code) newErrors.code = 'Tax code is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.taxTypeId) newErrors.taxTypeId = 'Tax type is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.currencyCode) newErrors.currencyCode = 'Currency is required';
    // OrgUnit validation
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Organisation Unit is required when scope is OrgUnit';
    }
    // Rate validation (if not exempt)
    if (!formData.isExempt) {
      if (formData.rate === '') newErrors.rate = 'Rate is required';
      if (formData.rate < 0 || formData.rate > 1) {
        newErrors.rate = 'Rate must be between 0 and 1 (e.g., 0.10 for 10%)';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      // If exempt, ensure rate is 0
      const finalFormData = {
        ...formData,
        rate: formData.isExempt ? 0 : parseFloat(formData.rate)
      };
      // If editing, preserve the ID and isActive
      if (taxCode) {
        onSave({
          ...taxCode,
          ...finalFormData
        });
      } else {
        onSave(finalFormData);
      }
    }
  };
  const handleToggle = () => {
    if (taxCode && onToggleStatus) {
      // Call the toggle status handler which updates the parent state
      onToggleStatus(taxCode.id);
      // Close the form and return to list view after toggling
      onCancel();
    }
  };
  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const getEventIcon = eventType => {
    switch (eventType) {
      case 'Activated':
      case 'Reactivated':
        return <UploadCloudIcon className="h-5 w-5 text-green-600" data-id="element-11914" />;
      case 'Deactivated':
        return <InfoIcon className="h-5 w-5 text-amber-600" data-id="element-11915" />;
      case 'Update':
        return <InfoIcon className="h-5 w-5 text-blue-600" data-id="element-11916" />;
      default:
        return <InfoIcon className="h-5 w-5 text-gray-600" data-id="element-11917" />;
    }
  };
  // Combine audit and lifecycle history for unified timeline
  const combinedHistory = [...auditHistory.map(entry => ({
    id: entry.id,
    type: 'audit',
    eventType: entry.action,
    eventDescription: entry.action === 'Update' ? `${entry.field} updated` : entry.action,
    timestamp: entry.timestamp,
    userName: entry.userName,
    changes: [{
      field: entry.field,
      oldValue: entry.oldValue,
      newValue: entry.newValue
    }]
  })), ...lifecycleHistory.map(entry => ({
    id: entry.id,
    type: 'lifecycle',
    eventType: entry.action,
    eventDescription: entry.action,
    timestamp: entry.performedAt,
    userName: entry.performedBy,
    changes: [],
    details: {
      activeFrom: entry.activeFrom,
      activeTo: entry.activeTo,
      effectiveDate: entry.effectiveDate
    }
  }))].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return <form onSubmit={handleSubmit} className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-11918">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11919">
        <div className="flex justify-between items-center" data-id="element-11920">
          <div data-id="element-11921">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11922">
              {taxCode ? 'Edit Tax Code' : 'Create New Tax Code'}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-11923">
              {taxCode ? 'Update the details of an existing tax code' : 'Enter the details to create a new tax code'}
            </p>
          </div>
          {activeTab === 'details' && <div className="flex space-x-3" data-id="element-11924">
              <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11925">
                <XIcon className="h-4 w-4 mr-1.5" data-id="element-11926" />
                Cancel
              </button>
              <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11927">
                <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-11928" />
                {taxCode ? 'Update Tax Code' : 'Create Tax Code'}
              </button>
            </div>}
        </div>
      </div>

      {/* Tabs - only show when editing */}
      {taxCode && <div className="border-b border-gray-200" data-id="element-11929">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-11930">
            <button type="button" onClick={() => setActiveTab('details')} className={`${activeTab === 'details' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-11931">
              Details
            </button>
            <button type="button" onClick={() => setActiveTab('history')} className={`${activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-11932">
              History
            </button>
          </nav>
        </div>}

      {/* Tab Content */}
      <div className="px-4 py-5 sm:p-6" data-id="element-11933">
        {activeTab === 'details' && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11934">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-11935">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-11936">
                Scope <span className="text-red-500" data-id="element-11937">*</span>
              </label>
              <div className="mt-1" data-id="element-11938">
                <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11939">
                  <option value="Global" data-id="element-11940">Global</option>
                  <option value="OrgUnit" data-id="element-11941">Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-11942">
                {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
              </p>
            </div>

            {/* OrgUnit Selector - Only shown when scope is OrgUnit */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-11943">
                <OrgUnitSelector value={formData.orgUnitId} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-11944" />
              </div>}

            {/* Basic Information */}
            <div className="sm:col-span-2" data-id="element-11945">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-11946">
                Code <span className="text-red-500" data-id="element-11947">*</span>
              </label>
              <div className="mt-1" data-id="element-11948">
                <input type="text" name="code" id="code" value={formData.code} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.code ? 'border-red-300' : ''}`} placeholder="e.g., GST10, VAT20" data-id="element-11949" />
                {errors.code && <p className="mt-1 text-sm text-red-600" data-id="element-11950">{errors.code}</p>}
              </div>
            </div>

            <div className="sm:col-span-4" data-id="element-11951">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-11952">
                Description <span className="text-red-500" data-id="element-11953">*</span>
              </label>
              <div className="mt-1" data-id="element-11954">
                <input type="text" name="description" id="description" value={formData.description} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.description ? 'border-red-300' : ''}`} placeholder="e.g., Goods and Services Tax" data-id="element-11955" />
                {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-11956">
                    {errors.description}
                  </p>}
              </div>
            </div>

            {/* Tax Type Selector */}
            <div className="sm:col-span-2" data-id="element-11957">
              <TaxTypeSelector value={formData.taxTypeId} onChange={handleTaxTypeChange} label="Tax Type" placeholder="Select tax type" required={true} error={errors.taxTypeId} orgUnitId={formData.orgUnitId} taxTypes={taxTypes} data-id="element-11958" />
            </div>

            <div className="sm:col-span-2" data-id="element-11959">
              <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-11960">
                Country <span className="text-red-500" data-id="element-11961">*</span>
              </label>
              <div className="mt-1" data-id="element-11962">
                <CountrySelector value={formData.country} onChange={handleCountryChange} locale={locale} data-id="element-11963" />
                {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-11964">{errors.country}</p>}
              </div>
            </div>

            <div className="sm:col-span-2" data-id="element-11965">
              <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700" data-id="element-11966">
                Jurisdiction
              </label>
              <div className="mt-1" data-id="element-11967">
                <input type="text" name="jurisdiction" id="jurisdiction" value={formData.jurisdiction} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g., New York, California" data-id="element-11968" />
              </div>
            </div>

            <div className="sm:col-span-2" data-id="element-11969">
              <label htmlFor="currencyCode" className="block text-sm font-medium text-gray-700" data-id="element-11970">
                Currency <span className="text-red-500" data-id="element-11971">*</span>
              </label>
              <div className="mt-1" data-id="element-11972">
                <CurrencySelector value={formData.currencyCode} onChange={handleCurrencyChange} countryCode={formData.country} locale={locale} data-id="element-11973" />
                {errors.currencyCode && <p className="mt-1 text-sm text-red-600" data-id="element-11974">
                    {errors.currencyCode}
                  </p>}
              </div>
            </div>

            <div className="sm:col-span-2" data-id="element-11975">
              <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-11976">
                Rate <span className="text-red-500" data-id="element-11977">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-11978">
                <input type="number" name="rate" id="rate" value={formData.rate} onChange={handleInputChange} disabled={formData.isExempt} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.rate ? 'border-red-300' : ''} ${formData.isExempt ? 'bg-gray-100' : ''}`} placeholder="0.10" step="0.01" min="0" max="1" data-id="element-11979" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-11980">
                  <span className="text-gray-500 sm:text-sm" data-id="element-11981">
                    {formData.isExempt ? 'Exempt' : '%'}
                  </span>
                </div>
              </div>
              {errors.rate ? <p className="mt-1 text-sm text-red-600" data-id="element-11982">{errors.rate}</p> : <p className="mt-1 text-xs text-gray-500" data-id="element-11983">
                  Enter as decimal (e.g., 0.10 for 10%)
                </p>}
            </div>

            <div className="sm:col-span-3" data-id="element-11984">
              <div className="flex items-center h-5" data-id="element-11985">
                <input id="isExempt" name="isExempt" type="checkbox" checked={formData.isExempt} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-11986" />
                <label htmlFor="isExempt" className="ml-2 block text-sm text-gray-700" data-id="element-11987">
                  Tax Exempt
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11988">
                When checked, the rate will be set to 0
              </p>
            </div>

            <div className="sm:col-span-3" data-id="element-11989">
              <div className="flex items-center h-5" data-id="element-11990">
                <input id="isInclusive" name="isInclusive" type="checkbox" checked={formData.isInclusive} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-11991" />
                <label htmlFor="isInclusive" className="ml-2 block text-sm text-gray-700" data-id="element-11992">
                  Tax Inclusive
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11993">
                When checked, the tax is included in the price
              </p>
            </div>

            {/* Toggle for advanced fields */}
            <div className="sm:col-span-6" data-id="element-11994">
              <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11995">
                {showAdvanced ? 'Hide Advanced Fields' : 'Show Advanced Fields'}
              </button>
            </div>

            {/* Advanced fields (collapsible) */}
            {showAdvanced && <>
                <div className="sm:col-span-6" data-id="element-11996">
                  <hr className="my-2" data-id="element-11997" />
                  <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-11998">
                    Advanced Settings
                  </h4>
                </div>

                <div className="sm:col-span-6" data-id="element-11999">
                  <label htmlFor="globalTaxDefinitionId" className="block text-sm font-medium text-gray-700" data-id="element-12000">
                    Global Tax Definition ID
                  </label>
                  <div className="mt-1" data-id="element-12001">
                    <input type="text" name="globalTaxDefinitionId" id="globalTaxDefinitionId" value={formData.globalTaxDefinitionId} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional reference ID" data-id="element-12002" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-12003">
                    Optional identifier for global tax definitions
                  </p>
                </div>

                {/* Display audit information if editing */}
                {taxCode && <>
                    <div className="sm:col-span-3" data-id="element-12004">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-12005">
                        Created By
                      </label>
                      <div className="mt-1" data-id="element-12006">
                        <input type="text" value={taxCode.createdBy} disabled className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-12007" />
                      </div>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-12008">
                        {new Date(taxCode.createdAt).toLocaleString(locale)}
                      </p>
                    </div>

                    <div className="sm:col-span-3" data-id="element-12009">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-12010">
                        Last Modified By
                      </label>
                      <div className="mt-1" data-id="element-12011">
                        <input type="text" value={taxCode.modifiedBy} disabled className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-12012" />
                      </div>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-12013">
                        {new Date(taxCode.modifiedAt).toLocaleString(locale)}
                      </p>
                    </div>
                  </>}
              </>}
          </div>}

        {/* History Tab */}
        {activeTab === 'history' && taxCode && <div className="py-4" data-id="element-12014">
            <div className="bg-white rounded-lg border border-gray-200" data-id="element-12015">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-12016">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-12017">
                  Tax Code History
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-12018">
                  {combinedHistory.length} events
                </p>
              </div>

              <div className="px-6 py-6" data-id="element-12019">
                {combinedHistory.length === 0 ? <div className="text-center py-12" data-id="element-12020">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-12021" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-12022">
                      No history
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-12023">
                      No changes have been recorded for this tax code yet.
                    </p>
                  </div> : <div className="flow-root" data-id="element-12024">
                    <ul className="-mb-8" data-id="element-12025">
                      {combinedHistory.map((event, eventIdx) => <li key={event.id} data-id="element-12026">
                          <div className="relative pb-8" data-id="element-12027">
                            {eventIdx !== combinedHistory.length - 1 && <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-12028" />}
                            <div className="relative flex items-start space-x-3" data-id="element-12029">
                              <div data-id="element-12030">
                                <div className="relative px-1" data-id="element-12031">
                                  <div className="h-10 w-10 bg-gray-100 rounded-full ring-8 ring-white flex items-center justify-center" data-id="element-12032">
                                    {getEventIcon(event.eventType)}
                                  </div>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-12033">
                                <div data-id="element-12034">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-12035">
                                    {event.eventDescription}
                                  </div>
                                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-12036">
                                    {formatTimestamp(event.timestamp)} by{' '}
                                    {event.userName}
                                  </p>
                                </div>
                                {event.changes && event.changes.length > 0 && <div className="mt-2 text-sm text-gray-700" data-id="element-12037">
                                    <div className="bg-gray-50 rounded-md p-3 space-y-1" data-id="element-12038">
                                      {event.changes.map((change, idx) => <div key={idx} className="flex items-center text-xs" data-id="element-12039">
                                          <span className="font-medium text-gray-700" data-id="element-12040">
                                            {change.field}:
                                          </span>
                                          <span className="ml-2 text-gray-500 line-through" data-id="element-12041">
                                            {change.oldValue}
                                          </span>
                                          <span className="mx-2 text-gray-400" data-id="element-12042">
                                            →
                                          </span>
                                          <span className="text-gray-900" data-id="element-12043">
                                            {change.newValue}
                                          </span>
                                        </div>)}
                                    </div>
                                  </div>}
                                {event.details && <div className="mt-2 text-sm text-gray-700" data-id="element-12044">
                                    <div className="bg-gray-50 rounded-md p-3 space-y-1" data-id="element-12045">
                                      <div className="text-xs" data-id="element-12046">
                                        <span className="font-medium text-gray-700" data-id="element-12047">
                                          Active From:
                                        </span>
                                        <span className="ml-2 text-gray-900" data-id="element-12048">
                                          {event.details.activeFrom}
                                        </span>
                                      </div>
                                      {event.details.activeTo && <div className="text-xs" data-id="element-12049">
                                          <span className="font-medium text-gray-700" data-id="element-12050">
                                            Active To:
                                          </span>
                                          <span className="ml-2 text-gray-900" data-id="element-12051">
                                            {event.details.activeTo}
                                          </span>
                                        </div>}
                                      <div className="text-xs" data-id="element-12052">
                                        <span className="font-medium text-gray-700" data-id="element-12053">
                                          Effective Date:
                                        </span>
                                        <span className="ml-2 text-gray-900" data-id="element-12054">
                                          {event.details.effectiveDate}
                                        </span>
                                      </div>
                                    </div>
                                  </div>}
                              </div>
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>}
      </div>

      {activeTab === 'details' && <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-center" data-id="element-12055">
          {/* Deactivate/Activate button - only shown when editing */}
          <div data-id="element-12056">
            {taxCode && onToggleStatus && <button type="button" onClick={handleToggle} className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${taxCode.isActive ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300 focus:ring-amber-500' : 'text-green-700 bg-green-50 hover:bg-green-100 border-green-300 focus:ring-green-500'}`} title={taxCode.isActive ? 'Deactivate' : 'Activate'} data-id="element-12057">
                {taxCode.isActive ? <>
                    <ToggleRightIcon className="h-4 w-4 mr-1.5" data-id="element-12058" />
                    Deactivate
                  </> : <>
                    <ToggleLeftIcon className="h-4 w-4 mr-1.5" data-id="element-12059" />
                    Activate
                  </>}
              </button>}
          </div>

          {/* Save and Cancel buttons */}
          <div className="flex space-x-3" data-id="element-12060">
            <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12061">
              <XIcon className="h-4 w-4 mr-1.5" data-id="element-12062" />
              Cancel
            </button>
            <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12063">
              <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-12064" />
              {taxCode ? 'Update Tax Code' : 'Create Tax Code'}
            </button>
          </div>
        </div>}
    </form>;
}